import { useId, useMemo, useState } from 'react';
import {
  buildTree,
  fieldValue,
  flattenTree,
  listInvestments,
  plannedSpend,
  replacedItems,
  today,
  type AnyEvent,
  type InvestmentEntry,
  type ItemRecord,
  type Manifest,
  type Money,
} from '@intentionalist/shared';
import {
  abandonInvestment,
  acquireInvestment,
  createInvestment,
  deleteInvestment,
  linkInvestment,
  setIntention,
  unlinkInvestment,
  updateInvestment,
} from './mutations.js';
import { formatAmount, parseMoney } from './money.js';
import { currencySuffix } from './currency-display.js';

/**
 * The investment list (Epic 8).
 *
 * The counterpart to the release queue, and deliberately a separate screen from
 * it. Releasing a thing and replacing it are different decisions, and a list
 * that mixed them would quietly turn a clear-out into a shopping trip.
 */
export function InvestmentList({
  manifest,
  version,
  onWrite,
}: {
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [closing, setClosing] = useState<{ id: string; how: 'acquired' | 'abandoned' } | null>(
    null,
  );

  const entries = useMemo(() => listInvestments(manifest), [manifest, version]);
  const planned = useMemo(() => plannedSpend(manifest), [manifest, version]);
  const categories = useMemo(() => buildTree(manifest.categories), [manifest, version]);
  const open = entries.filter((entry) => entry.status === 'open');

  return (
    <div className="review">
      <h2>Investments</h2>
      <p className="muted hint">
        Things you have decided you need, kept apart from the catalog until you own them.
      </p>

      <dl className="sync-state">
        <dt>Still considering</dt>
        <dd>{open.length}</dd>

        <dt>Reference prices total</dt>
        {/* Grouped by currency and never added across them, the same rule the
            release report follows. */}
        <dd>
          {planned.length === 0 ? (
            <span className="muted">—</span>
          ) : (
            planned.map((total, index) => (
              <span key={total.currency}>
                {index > 0 && ' · '}
                {formatAmount(total, { ambiguous: planned.length > 1 })}
              </span>
            ))
          )}
        </dd>
      </dl>

      {adding ? (
        <EntryForm
          manifest={manifest}
          categories={categories}
          onCancel={() => setAdding(false)}
          onSubmit={(draft, replaces) => {
            onWrite(createInvestment(draft, replaces).events);
            setAdding(false);
          }}
        />
      ) : (
        <div className="actions">
          <button type="button" onClick={() => setAdding(true)}>
            Add something
          </button>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="muted">
          Nothing here yet. Items you mark Invest can start an entry, or add one above.
        </p>
      ) : (
        <ul className="catalog" aria-label="Investment list">
          {entries.map((entry) => (
            <li key={entry.investmentId}>
              <div className="catalog-row">
                <div>
                  <strong>{entry.name}</strong>
                  <div className="muted">
                    <Standing entry={entry} />
                  </div>
                  {entry.notes !== null && <div className="muted">{entry.notes}</div>}
                  <Replaces manifest={manifest} entry={entry} onWrite={onWrite} />
                </div>

                <div className="actions">
                  {entry.status === 'open' && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setClosing(
                            closing?.id === entry.investmentId && closing.how === 'acquired'
                              ? null
                              : { id: entry.investmentId, how: 'acquired' },
                          )
                        }
                      >
                        I bought it
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setClosing(
                            closing?.id === entry.investmentId && closing.how === 'abandoned'
                              ? null
                              : { id: entry.investmentId, how: 'abandoned' },
                          )
                        }
                      >
                        Not any more
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setEditing(editing === entry.investmentId ? null : entry.investmentId)
                    }
                  >
                    {editing === entry.investmentId ? 'Cancel' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => onWrite(deleteInvestment(entry.investmentId))}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {editing === entry.investmentId && (
                <EntryForm
                  manifest={manifest}
                  categories={categories}
                  entry={entry}
                  onCancel={() => setEditing(null)}
                  onSubmit={(draft) => {
                    onWrite(updateInvestment(entry.investmentId, draft));
                    setEditing(null);
                  }}
                />
              )}

              {closing?.id === entry.investmentId && closing.how === 'acquired' && (
                <Acquire
                  manifest={manifest}
                  entry={entry}
                  onWrite={onWrite}
                  onDone={() => setClosing(null)}
                />
              )}

              {closing?.id === entry.investmentId && closing.how === 'abandoned' && (
                <Abandon
                  onCancel={() => setClosing(null)}
                  onDone={(events) => {
                    onWrite(events);
                    setClosing(null);
                  }}
                  investmentId={entry.investmentId}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Where an entry stands, and how long it took to get there (FR-8.7). */
function Standing({ entry }: { entry: InvestmentEntry }) {
  const days =
    entry.daysOpen === null ? null : `${entry.daysOpen} day${entry.daysOpen === 1 ? '' : 's'}`;

  if (entry.status === 'acquired') {
    return <>{`Bought ${entry.acquiredOn ?? ''}${days === null ? '' : ` · considered ${days}`}`}</>;
  }
  if (entry.status === 'abandoned') {
    return (
      <>
        {`Let go ${entry.abandonedOn ?? ''}${days === null ? '' : ` · considered ${days}`}`}
        {entry.abandonReason !== null && ` · ${entry.abandonReason}`}
      </>
    );
  }
  return (
    <>
      {entry.referencePrice !== null && `${formatAmount(entry.referencePrice)} · `}
      {entry.priority > 0 && `priority ${entry.priority} · `}
      {days === null ? 'open' : `open ${days}`}
    </>
  );
}

/** FR-8.2: what this would replace, with the means to add to it or say otherwise. */
function Replaces({
  manifest,
  entry,
  onWrite,
}: {
  manifest: Manifest;
  entry: InvestmentEntry;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const items = replacedItems(manifest, entry);
  const candidates = replaceable(manifest).filter((item) => !entry.replaces.includes(item.itemId));

  return (
    <>
      {items.length > 0 && (
        <ul className="chips" aria-label={`Replaces, for ${entry.name}`}>
          {items.map((item) => (
            <li key={item.itemId}>
              {String(fieldValue(item, 'name') ?? '(unnamed)')}
              <button
                type="button"
                aria-label={`Unlink ${String(fieldValue(item, 'name') ?? 'item')}`}
                onClick={() => onWrite(unlinkInvestment(entry.investmentId, item.itemId))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* An entry often starts before the thing it replaces is identified, and
          only open entries can still change what they are for. */}
      {entry.status === 'open' && candidates.length > 0 && (
        <select
          aria-label={`Link an item to ${entry.name}`}
          value=""
          onChange={(e) => onWrite(linkInvestment(entry.investmentId, e.target.value))}
        >
          <option value="">Link what it replaces…</option>
          {candidates.map((item) => (
            <option key={item.itemId} value={item.itemId}>
              {String(fieldValue(item, 'name') ?? '(unnamed)')}
            </option>
          ))}
        </select>
      )}
    </>
  );
}

interface Draft {
  name: string;
  notes?: string;
  categoryId?: string | null;
  referencePrice?: Money | null;
  priority?: number;
}

function EntryForm({
  manifest,
  categories,
  entry,
  onCancel,
  onSubmit,
}: {
  manifest: Manifest;
  categories: ReturnType<typeof buildTree>;
  entry?: InvestmentEntry;
  onCancel: () => void;
  onSubmit: (draft: Draft, replaces: string[]) => void;
}) {
  const ids = {
    name: useId(),
    notes: useId(),
    category: useId(),
    price: useId(),
    priority: useId(),
    replaces: useId(),
  };
  const [name, setName] = useState(entry?.name ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [categoryId, setCategoryId] = useState(entry?.categoryId ?? '');
  const [price, setPrice] = useState(
    entry?.referencePrice === undefined || entry.referencePrice === null
      ? ''
      : (entry.referencePrice.minor / 100).toFixed(2),
  );
  const [priority, setPriority] = useState(String(entry?.priority ?? 0));
  const [replaces, setReplaces] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => replaceable(manifest), [manifest]);

  const submit = () => {
    if (name.trim() === '') {
      setError('It needs a name.');
      return;
    }

    let referencePrice: Money | null = null;
    if (price.trim() !== '') {
      referencePrice = parseMoney(price);
      if (referencePrice === null) {
        setError('That amount is not a number. Try something like 300.00.');
        return;
      }
    }

    onSubmit(
      {
        name,
        notes,
        categoryId: categoryId === '' ? null : categoryId,
        referencePrice,
        priority: Number(priority),
      },
      replaces,
    );
  };

  return (
    <div className="grid-fields">
      <div className="field">
        <label htmlFor={ids.name}>{entry === undefined ? 'What do you need' : 'Name'}</label>
        <input
          id={ids.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.notes}>Notes</label>
        {/* FR-8.6: research lives here rather than in a separate field, so there
            is one place to look rather than two to keep in step. */}
        <textarea
          id={ids.notes}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>

      <div className="field">
        <label htmlFor={ids.category}>Category</label>
        <select
          id={ids.category}
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          <option value="">None</option>
          {flattenTree(categories).map((node) => (
            <option key={node.id} value={node.id}>
              {'  '.repeat(node.depth - 1)}
              {node.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={ids.price}>
          Reference price
          {currencySuffix()}
        </label>
        <input
          id={ids.price}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputMode="decimal"
          placeholder="300.00"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.priority}>Priority</label>
        <select id={ids.priority} value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="0">unset</option>
          <option value="1">someday</option>
          <option value="5">soon</option>
          <option value="9">next</option>
        </select>
      </div>

      {/* Only offered while creating: an existing entry edits its links through
          the chips on the row, which is where they are shown. */}
      {entry === undefined && candidates.length > 0 && (
        <div className="field">
          <label htmlFor={ids.replaces}>Would replace</label>
          <select
            id={ids.replaces}
            multiple
            value={replaces}
            onChange={(e) =>
              setReplaces([...e.target.selectedOptions].map((option) => option.value))
            }
          >
            {candidates.map((item) => (
              <option key={item.itemId} value={item.itemId}>
                {String(fieldValue(item, 'name') ?? '(unnamed)')}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <p className="warn" role="alert" aria-live="polite">
          {error ?? ''}
        </p>
        <div className="actions">
          <button type="button" className="primary" onClick={submit}>
            {entry === undefined ? 'Add' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Recording a purchase (FR-8.4).
 *
 * Two steps on purpose. The first creates the catalog item, because that is
 * what buying the thing means. The second *offers* to mark what it replaces for
 * release and does nothing unless it is taken: a new coat does not oblige anyone
 * to give away the old one (NFR-SAFE-1).
 */
function Acquire({
  manifest,
  entry,
  onWrite,
  onDone,
}: {
  manifest: Manifest;
  entry: InvestmentEntry;
  onWrite: (events: AnyEvent[]) => void;
  onDone: () => void;
}) {
  const ids = { name: useId(), date: useId(), price: useId() };
  const [name, setName] = useState(entry.name);
  const [onDate, setOnDate] = useState(today());
  const [price, setPrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [offer, setOffer] = useState<ItemRecord[] | null>(null);

  const submit = () => {
    if (name.trim() === '') {
      setError('The new item needs a name.');
      return;
    }

    let purchasePrice: Money | null = null;
    if (price.trim() !== '') {
      purchasePrice = parseMoney(price);
      if (purchasePrice === null) {
        setError('That amount is not a number. Try something like 300.00.');
        return;
      }
    }

    const replaced = replacedItems(manifest, entry).filter(
      (item) => item.currentIntention !== 'release',
    );

    onWrite(
      acquireInvestment(
        entry.investmentId,
        {
          name: name.trim(),
          acquiredOn: onDate,
          ...(purchasePrice === null ? {} : { purchasePrice }),
          ...(entry.categoryId === null ? {} : { categoryId: entry.categoryId }),
        },
        onDate,
      ).events,
    );

    if (replaced.length === 0) {
      onDone();
      return;
    }
    setOffer(replaced);
  };

  if (offer !== null) {
    return (
      <div className="field">
        <p>
          You have the new one. Mark{' '}
          {offer.map((item, index) => (
            <span key={item.itemId}>
              {index > 0 && ', '}
              <strong>{String(fieldValue(item, 'name') ?? '(unnamed)')}</strong>
            </span>
          ))}{' '}
          for release?
        </p>
        <div className="actions">
          <button
            type="button"
            onClick={() => {
              onWrite(offer.flatMap((item) => setIntention(item.itemId, 'release')));
              onDone();
            }}
          >
            Yes, mark for release
          </button>
          {/* Declining is a real answer, not a dismissal: keeping both is a
              legitimate outcome and the entry is closed either way. */}
          <button type="button" onClick={onDone}>
            No, I am keeping it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid-fields">
      <div className="field">
        <label htmlFor={ids.name}>Name it in the catalog</label>
        <input
          id={ids.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.date}>Bought on</label>
        <input
          id={ids.date}
          type="date"
          value={onDate}
          onChange={(e) => setOnDate(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={ids.price}>Paid</label>
        <input
          id={ids.price}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputMode="decimal"
          placeholder="300.00"
        />
      </div>

      <div className="field">
        <p className="warn" role="alert" aria-live="polite">
          {error ?? ''}
        </p>
        <button type="button" onClick={submit}>
          Add to catalog
        </button>
      </div>
    </div>
  );
}

/** FR-8.5. The reason is the useful part, which is why it is asked for and not required. */
function Abandon({
  investmentId,
  onCancel,
  onDone,
}: {
  investmentId: string;
  onCancel: () => void;
  onDone: (events: AnyEvent[]) => void;
}) {
  const ids = { reason: useId(), date: useId() };
  const [reason, setReason] = useState('');
  const [onDate, setOnDate] = useState(today());

  return (
    <div className="grid-fields">
      <div className="field">
        <label htmlFor={ids.reason}>Why not?</label>
        <input
          id={ids.reason}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoComplete="off"
          placeholder="Mended the old one"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.date}>Decided on</label>
        <input
          id={ids.date}
          type="date"
          value={onDate}
          onChange={(e) => setOnDate(e.target.value)}
        />
      </div>

      <div className="field">
        <div className="actions">
          <button
            type="button"
            className="primary"
            onClick={() => onDone(abandonInvestment(investmentId, onDate, reason))}
          >
            Record
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Items an entry could plausibly replace.
 *
 * Everything active, because the thing being replaced is not always one the user
 * has already judged -- realizing the old one is inadequate is often what starts
 * the entry in the first place.
 */
function replaceable(manifest: Manifest): ItemRecord[] {
  const items = [...manifest.items.values()].filter(
    (item) => item.lifecycle?.present !== false && item.release === null,
  );
  return items.sort((a, b) =>
    String(fieldValue(a, 'name') ?? '').localeCompare(String(fieldValue(b, 'name') ?? '')),
  );
}
